import {
  Badge,
  Box,
  Flex,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  approvalColor,
  approvalLabel,
  formatDollars,
  formatFullDate,
  formatJsDate,
  formatPeriodShort,
  statusColor,
} from '~/backup/shared/format';
import {
  awaitingPeriodColor,
  awaitingPeriodLabel,
  formatInputStatuses,
  hasFailedInput,
} from '~/backup/shared/servicingPeriods';
import { PeriodDocumentsCell } from '~/backup/shared/views/deal-overview/periodFiles';
import {
  DealPeriod,
  DealPeriodCycle,
  periodPath,
} from '~/backup/shared/views/deal-overview/types';

function ValidationCell({
  status,
}: {
  status: string | null | undefined;
}): React.ReactElement {
  const label = (status ?? 'pending').toUpperCase();
  return (
    <Text fontWeight={600} fontSize="0.8rem" color={statusColor(status ?? '')}>
      {label}
    </Text>
  );
}

/**
 * The servicer's accept/reject decision on the cycle. Its own column because
 * validation (the automated tie-out) and review (the human decision) are
 * different gates that can disagree — a cycle can tie out and still be returned.
 */
function ReviewCell({
  approvalStatus,
}: {
  approvalStatus: string;
}): React.ReactElement {
  return (
    <Text
      fontWeight={600}
      fontSize="0.8rem"
      color={approvalColor(approvalStatus)}
    >
      {approvalLabel(approvalStatus).toUpperCase()}
    </Text>
  );
}

/** The columns after Period for a reconciled period: the cycle's own state. */
function CycleCells({
  period,
  cycle,
}: {
  period: DealPeriod;
  cycle: DealPeriodCycle;
}): React.ReactElement {
  return (
    <>
      <Td>
        <Text fontSize="0.8rem">{formatJsDate(cycle.tapeReceivedAt)}</Text>
        <Text fontSize="0.75rem" color={colors.gray[500]}>
          {(cycle.tapeRowCount ?? 0).toLocaleString()} rows ·{' '}
          {formatDollars(cycle.tapeTotalPrincipal)}
        </Text>
        {/* The inputs follow the latest upload, the cycle the one it reconciled:
            a failed re-send leaves the cycle as it was, so say so. */}
        {hasFailedInput(period) && (
          <Text fontSize="0.75rem" color={colors.red[400]}>
            Latest upload failed
          </Text>
        )}
      </Td>
      <Td>
        <ValidationCell status={cycle.reconcileStatus} />
      </Td>
      <Td>
        <ReviewCell approvalStatus={cycle.approvalStatus} />
      </Td>
      <Td>
        {/* A rejected cycle never acquires a `certificateStatus`, so spell out
            that none is coming rather than showing a dash. */}
        <Text
          fontSize="0.8rem"
          color={
            !cycle.certificateStatus && cycle.approvalStatus === 'rejected'
              ? approvalColor(cycle.approvalStatus)
              : undefined
          }
        >
          {cycle.certificateStatus?.toUpperCase() ??
            (cycle.approvalStatus === 'rejected' ? 'NOT ISSUED' : '—')}
        </Text>
        {cycle.certificateIssuedAt !== null && (
          <Text fontSize="0.75rem" color={colors.gray[500]}>
            Issued {formatJsDate(cycle.certificateIssuedAt)}
          </Text>
        )}
      </Td>
      <Td>
        <Text fontSize="0.8rem" color={colors.gray[500]}>
          {formatJsDate(cycle.msrReceivedAt)}
        </Text>
      </Td>
    </>
  );
}

/**
 * The columns after Period for a period with no cycle yet: nothing has
 * reconciled, so one cell says what it waits on and where each input stands.
 */
function AwaitingCells({
  period,
  today,
}: {
  period: DealPeriod;
  today: string;
}): React.ReactElement {
  return (
    <Td colSpan={5}>
      <Text
        fontWeight={600}
        fontSize="0.8rem"
        color={awaitingPeriodColor(period, today)}
      >
        {awaitingPeriodLabel(period, today)}
      </Text>
      <Text fontSize="0.75rem" color={colors.gray[500]}>
        Due {formatFullDate(period.dueDate)} ·{' '}
        {formatInputStatuses(period.inputs)}
      </Text>
    </Td>
  );
}

/**
 * Every period of the deal, newest first, for scanning months side by side. A
 * period's name selects it on this page; the one in view is highlighted.
 */
export function PeriodsTable({
  implementationId,
  periods,
  current,
  selected,
  today,
}: {
  implementationId: string;
  periods: DealPeriod[];
  current: DealPeriod | null;
  selected: DealPeriod;
  today: string;
}): React.ReactElement {
  // Certified = the certificate has been issued. BUS certificates are pre-signed,
  // so an issued certificate is a signed certificate.
  const certifiedCount = periods.filter(
    (p) => p.cycle?.certificateStatus === 'issued',
  ).length;

  return (
    <Box>
      <Flex justify="space-between" align="baseline" mb="0.75rem">
        <Typography variant="section-title">All periods</Typography>
        <Text color={colors.gray[500]} fontSize="0.8rem">
          {certifiedCount} of {periods.length} period
          {periods.length === 1 ? '' : 's'} certified
        </Text>
      </Flex>
      <TableContainer
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Period</Th>
              <Th>Tape</Th>
              <Th>Validation</Th>
              <Th>Review</Th>
              <Th>Certificate</Th>
              <Th>MSR</Th>
              <Th>Documents</Th>
            </Tr>
          </Thead>
          <Tbody>
            {periods.map((period) => {
              const isSelected =
                period.reportingPeriod === selected.reportingPeriod;
              return (
                <Tr
                  key={period.reportingPeriod}
                  bg={isSelected ? colors.blue[50] : undefined}
                  _hover={{ bg: isSelected ? undefined : colors.gray[50] }}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <Td fontWeight={600}>
                    <Flex align="center" gap="0.4rem">
                      <Link
                        as={RouterLink}
                        to={periodPath({ implementationId, period, current })}
                        color={colors.blue[400]}
                      >
                        {formatPeriodShort(period.reportingPeriod)}
                      </Link>
                      {period.reportingPeriod === current?.reportingPeriod && (
                        <Badge colorScheme="blue" fontSize="0.6rem">
                          Current
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  {period.cycle ? (
                    <CycleCells period={period} cycle={period.cycle} />
                  ) : (
                    <AwaitingCells period={period} today={today} />
                  )}
                  <Td>
                    <PeriodDocumentsCell period={period} />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
